import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Printer, Plus, Search, ChevronRight, RefreshCw, XCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";
import {
  BackLink,
  HeroStatus,
  Section,
  SectionLabel,
  EntityCard,
  Card,
  CardTitle,
  KvGrid,
  KvItem,
  DetailList,
  FooterActions,
  ActionButton,
  TechDetailsToggle,
  SelectionTable,
  Alert,
  ProgressCard,
  fmtItalianDateTime,
  timeAgo,
  useNow,
  orDash,
  upperOrDash,
  EM_DASH,
} from "./Primitives";

/* ============================================================
 * Defensive accessors
 * ============================================================ */

const pickStep1 = (rd) => {
  if (!rd || typeof rd !== "object") return null;
  const immobili = rd.immobili || rd.unita_immobiliari || rd.lista_immobili;
  if (!Array.isArray(immobili) || immobili.length === 0) return null;
  return {
    id_indirizzo: rd.id_indirizzo,
    indirizzo: rd.indirizzo_normalizzato || rd.indirizzo,
    comune: rd.comune,
    provincia: rd.provincia,
    cap: rd.cap,
    immobili,
  };
};

const pickStep2 = (rd) => {
  if (!rd || typeof rd !== "object") return null;
  const im = rd.immobile || rd.dettaglio_immobile || rd.dati_immobile;
  if (!im) return null;
  return {
    immobile: im,
    intestatari: Array.isArray(rd.intestatari) ? rd.intestatari : [],
  };
};

const fmtEuroIt = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return EM_DASH;
  return `€ ${Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatConsistenza = (im) => {
  const v = im?.consistenza_vani;
  const mq = im?.consistenza_mq;
  const parts = [];
  if (v !== undefined && v !== null && v !== "") {
    const vNum = Number(v);
    parts.push(
      `${vNum.toLocaleString("it-IT", { maximumFractionDigits: 1 })} ${vNum === 1 ? "vano" : "vani"}`
    );
  }
  if (mq !== undefined && mq !== null && mq !== "") parts.push(`${mq} m²`);
  return parts.length ? parts.join(" · ") : EM_DASH;
};

const formatCategoria = (im) => {
  const cat = im?.categoria;
  const desc = im?.categoria_descrizione;
  if (!cat) return EM_DASH;
  return desc ? `${cat} — ${desc}` : cat;
};

const formatIndirizzoCompleto = (im) => {
  const piano = im?.piano ? `Piano ${im.piano}` : null;
  const interno = im?.interno ? `interno ${im.interno}` : null;
  const pi = [piano, interno].filter(Boolean).join(", ");
  const base = im?.indirizzo || "";
  return [base, pi].filter(Boolean).join(" — ") || EM_DASH;
};

const downloadCatastoPdf = async (request) => {
  if (!request?.id) return;
  try {
    const token = localStorage.getItem("wattline_token");
    const res = await fetch(`${API}/requests/${request.id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { toast.error("Errore download PDF"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `verifica_catastale_${request.id.slice(0, 8)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Errore download PDF"); }
};

const buildIntestatarioRow = (it) => {
  if (!it) return null;
  const isPF = it?.tipo === "PF" || (it?.nome && it?.cognome);
  const name = isPF
    ? [it.nome, it.cognome].filter(Boolean).join(" ")
    : it.denominazione || it.ragione_sociale || EM_DASH;
  const idCode = it.cf || it.piva || it.codice_fiscale;
  const quotaTxt = it.quota || it.quota_possesso;
  const parts = [];
  if (idCode) parts.push(`${isPF ? "C.F." : "P.IVA"} ${String(idCode).toUpperCase()}`);
  if (quotaTxt) parts.push(`Quota ${quotaTxt}`);
  return {
    main: name,
    sub: parts.join(" · "),
    count: it.titolo || it.tipo_diritto || null,
  };
};

/* ============================================================
 * Step 1 - selezione immobile
 * ============================================================ */

const Step1 = ({ request, step1, onConfirmSelection, onNewAddressSearch }) => {
  const [selectedId, setSelectedId] = useState(null);

  const indirizzoEntity = step1.indirizzo || EM_DASH;
  const sub = [
    step1.cap,
    step1.comune ? `${step1.comune}${step1.provincia ? ` (${step1.provincia})` : ""}` : null,
    "Italia",
  ]
    .filter(Boolean)
    .join(" · ");

  const items = step1.immobili.map((im, i) => {
    const id = im.id_immobile || im.id || im.subalterno || `row-${i}`;
    const piano = im.piano ? `Piano ${im.piano}` : null;
    const interno = im.interno ? `interno ${im.interno}` : null;
    const main = [piano, interno].filter(Boolean).join(", ") || `Immobile ${i + 1}`;
    const subParts = [];
    if (im.foglio) subParts.push(`Foglio ${im.foglio}`);
    if (im.particella) subParts.push(`Particella ${im.particella}`);
    if (im.subalterno) subParts.push(`Sub ${im.subalterno}`);
    return {
      id,
      main,
      sub: subParts.join(" · "),
      rightTop: im.categoria ? `Categoria ${im.categoria}` : null,
      rightBottom: im.consistenza_mq ? `${im.consistenza_mq} m²` : null,
    };
  });

  const handleConfirm = () => {
    if (!selectedId) return;
    const picked = step1.immobili.find(
      (im, i) => (im.id_immobile || im.id || im.subalterno || `row-${i}`) === selectedId
    );
    if (onConfirmSelection) onConfirmSelection({ id_indirizzo: step1.id_indirizzo, id_immobile: selectedId, immobile: picked });
    else {
      console.log("[catasto-indirizzo] selezione confermata:", { id_indirizzo: step1.id_indirizzo, id_immobile: selectedId, immobile: picked });
      toast.info("Selezione confermata (handler placeholder - step 2 backend non ancora cablato)");
    }
  };

  return (
    <div className="max-w-3xl" data-testid="result-indirizzo-step1">
      <BackLink to="/history" label="Storico richieste" />

      <HeroStatus
        variant="info"
        title="Indirizzo normalizzato"
        description={`Trovati ${step1.immobili.length} immobili a questo indirizzo. Seleziona quale verificare per procedere.`}
        testId="result-hero"
      />

      <Section>
        <EntityCard
          title="Indirizzo cercato"
          entity={indirizzoEntity}
          entitySize="md"
          sub={sub}
        />
      </Section>

      <Section>
        <SectionLabel>Seleziona immobile</SectionLabel>
        <SelectionTable items={items} selected={selectedId} onSelect={setSelectedId} />
      </Section>

      <Section>
        <Alert variant="warning">
          La verifica catastale dell'immobile selezionato è una richiesta a pagamento. Conferma per procedere.
        </Alert>
      </Section>

      <FooterActions>
        <ActionButton variant="primary" icon={FileText} onClick={() => downloadCatastoPdf(request)} testId="action-download-pdf">
          Scarica report PDF
        </ActionButton>
        <ActionButton
          variant="outline"
          icon={ChevronRight}
          disabled={!selectedId}
          onClick={handleConfirm}
          testId="action-confirm-selection"
        >
          Verifica immobile selezionato
        </ActionButton>
        <ActionButton variant="outline" icon={Search} onClick={onNewAddressSearch} testId="action-new-address">
          Cerca altro indirizzo
        </ActionButton>
      </FooterActions>

      <TechDetailsToggle data={request} />
    </div>
  );
};

/* ============================================================
 * Pending - in attesa risultato step 2
 * ============================================================ */

const Pending = ({ request, onManualRefresh }) => {
  useNow(10000);
  return (
    <div className="max-w-3xl" data-testid="result-indirizzo-pending">
      <BackLink to="/history" label="Storico richieste" />
      <HeroStatus
        variant="info"
        title="Visura catastale in lavorazione"
        description="Stiamo recuperando i dati catastali dell'immobile selezionato · controlliamo ogni 30 secondi"
      />
      <Section>
        <ProgressCard
          title="Attesa risposta dal Catasto"
          description={`Richiesta inviata ${timeAgo(request?.created_at)}. Tempo medio di elaborazione: 30–90 secondi.`}
        />
      </Section>
      <FooterActions>
        <ActionButton variant="outline" icon={RefreshCw} onClick={onManualRefresh} testId="action-refresh">
          Aggiorna stato
        </ActionButton>
        <ActionButton variant="ghost" icon={XCircle} onClick={() => {}} testId="action-cancel">
          Annulla richiesta
        </ActionButton>
      </FooterActions>
      <TechDetailsToggle data={request} />
    </div>
  );
};

/* ============================================================
 * Step 2 - dettaglio catastale completo
 * ============================================================ */

const Step2 = ({ request, step2, onNewSearch }) => {
  const navigate = useNavigate();
  const completedAt = request?.completed_at || request?.created_at;
  const im = step2.immobile || {};
  const intestatari = step2.intestatari || [];

  const indirizzoLabel = formatIndirizzoCompleto(im);
  const sub = [
    im.cap,
    im.comune ? `${im.comune}${im.provincia ? ` (${im.provincia})` : ""}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const intestatarioRows = intestatari.map(buildIntestatarioRow).filter(Boolean);

  const onExportJson = () => {
    try {
      const payload = {
        request_id: request?.id,
        service_id: request?.service_id,
        completed_at: completedAt,
        params: request?.params,
        response_data: request?.response_data,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `catasto_indirizzo_${request?.id || "result"}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore export JSON");
    }
  };

  const onPrint = () => window.print();
  const handleNewSearch = () => (onNewSearch ? onNewSearch() : navigate("/search/catasto"));

  return (
    <div className="max-w-3xl" data-testid="result-indirizzo-step2">
      <BackLink to="/history" label="Storico richieste" />

      <HeroStatus
        variant="success"
        title="Dati catastali recuperati"
        description={`Catasto Fabbricati · ${fmtItalianDateTime(completedAt)}`}
      />

      <Section>
        <EntityCard
          title="Immobile"
          entity={indirizzoLabel}
          entitySize="md"
          sub={sub}
        />
      </Section>

      <Section>
        <Card>
          <CardTitle>Identificazione catastale</CardTitle>
          <KvGrid columns={2}>
            <KvItem label="Foglio" value={orDash(im.foglio)} mono />
            <KvItem label="Particella" value={orDash(im.particella)} mono />
            <KvItem label="Subalterno" value={orDash(im.subalterno)} mono />
            <KvItem label="Codice catastale comune" value={orDash(im.comune_catastale || im.codice_comune)} mono />
          </KvGrid>
        </Card>
      </Section>

      <Section>
        <Card>
          <CardTitle>Caratteristiche</CardTitle>
          <KvGrid columns={2}>
            <KvItem label="Categoria" value={formatCategoria(im)} />
            <KvItem label="Classe" value={orDash(im.classe)} />
            <KvItem label="Consistenza" value={formatConsistenza(im)} />
            <KvItem label="Rendita catastale" value={fmtEuroIt(im.rendita)} />
            <KvItem label="Zona censuaria" value={orDash(im.zona_censuaria)} />
            <KvItem label="Piano" value={orDash(im.piano)} />
          </KvGrid>
        </Card>
      </Section>

      <Section>
        <Card>
          <CardTitle>Intestatari</CardTitle>
          {intestatarioRows.length === 0 ? (
            <div className="text-sm text-text-muted py-4">Nessun intestatario disponibile</div>
          ) : (
            <DetailList items={intestatarioRows} testId="detail-list-intestatari" />
          )}
        </Card>
      </Section>

      <FooterActions>
        <ActionButton variant="primary" icon={FileText} onClick={() => downloadCatastoPdf(request)} testId="action-download-pdf">
          Scarica report PDF
        </ActionButton>
        <ActionButton variant="outline" icon={Plus} onClick={handleNewSearch} testId="action-new-search">
          Nuova ricerca
        </ActionButton>
        <ActionButton variant="outline" icon={Download} onClick={onExportJson} testId="action-export-json">
          Esporta JSON
        </ActionButton>
        <ActionButton variant="outline" icon={Printer} onClick={onPrint} testId="action-print">
          Stampa
        </ActionButton>
      </FooterActions>

      <TechDetailsToggle data={request} />
    </div>
  );
};

/* ============================================================
 * Main switcher
 * ============================================================ */

export const ResultCatastoIndirizzo = ({ request, onConfirmSelection, onNewSearch, onManualRefresh }) => {
  const rd = request?.response_data || {};
  const status = request?.status;

  const step2 = pickStep2(rd);
  if (step2 && status === "completed") {
    return <Step2 request={request} step2={step2} onNewSearch={onNewSearch} />;
  }

  if (status === "pending_external" || status === "in_progress") {
    return <Pending request={request} onManualRefresh={onManualRefresh} />;
  }

  const step1 = pickStep1(rd);
  if (step1) {
    return (
      <Step1
        request={request}
        step1={step1}
        onConfirmSelection={onConfirmSelection}
        onNewAddressSearch={onNewSearch}
      />
    );
  }

  // Fallback minimale (es. status failed o response vuota)
  return (
    <div className="max-w-3xl" data-testid="result-indirizzo-fallback">
      <BackLink to="/history" label="Storico richieste" />
      <HeroStatus
        variant={status === "failed" ? "danger" : "warning"}
        title={status === "failed" ? "Ricerca fallita" : "Nessun risultato"}
        description={request?.error_message || "Nessun immobile trovato per l'indirizzo cercato."}
      />
      <TechDetailsToggle data={request} />
    </div>
  );
};
